'use client';

import React, { memo, useCallback, useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Delete,
  NextKeyframeFill,
  PreviousKeyframeFill,
} from '@crabs-ai/web-icons';
import { ToolButton, ValueInputField } from '@crabs-ai/ds-react';
import { PlayerRefContext } from '../../context-provider';
import { resolveGroupGeometry } from '../../items/group/group-geometry';
import {
  findNextKeyframe,
  findPrevKeyframe,
  getPositionOffsetAt,
  hasKeyframeAt,
  removeKeyframeAt,
  upsertPositionKeyframe,
  type PositionKeyframe,
} from '../../items/animation/position-keyframes';
import { changeItem } from '../../state/actions/change-item';
import { EditorStarterItem } from '../../items/item-type';
import { useAllItems, useItem, useWriteContext } from '../../utils/use-context';
import { useCurrentPlayerFrame } from '../../utils/use-current-player-frame';

/**
 * 위치 키프레임 다이아 마커 — 채우기/윤곽으로 "이 프레임에 키프레임 있음/없음" 을 표시.
 * DS 에 단독 다이아 아이콘이 없어 인라인 SVG 로 그린다(모션 패스 점과 같은 에디터 그래픽).
 * 색은 currentColor 이므로 text-* 로 제어한다.
 */
const KeyframeDiamond: React.FC<{ filled: boolean; className?: string }> = ({
  filled,
  className,
}) => (
  <svg
    viewBox="0 0 16 16"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M8 1.5 14.5 8 8 14.5 1.5 8Z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * 헤더 컨트롤 행 — [‹ 이전] [◆/◇ 토글] [› 다음] … [🗑 전체삭제].
 * 현재 플레이어 프레임에 반응하므로(useCurrentPlayerFrame) 재생 중 이 소형 컴포넌트만
 * 리렌더된다 — 상위(PositionKeyframeControls)로 프레임 갱신을 전파하지 않는다.
 */
const PositionKeyframeHeaderControls: React.FC<{
  keyframes: PositionKeyframe[];
  from: number;
  maxLocalFrame: number;
  onSeekToLocalFrame: (localFrame: number) => void;
  onAddAt: (localFrame: number) => void;
  onRemoveAt: (localFrame: number) => void;
  onRemoveAll: () => void;
}> = ({
  keyframes,
  from,
  maxLocalFrame,
  onSeekToLocalFrame,
  onAddAt,
  onRemoveAt,
  onRemoveAll,
}) => {
  const t = useTranslations();
  const globalFrame = useCurrentPlayerFrame();
  const localFrame = Math.round(globalFrame - from);
  const isInRange = localFrame >= 0 && localFrame <= maxLocalFrame;
  const hasKf = isInRange && hasKeyframeAt(keyframes, localFrame);
  const prevKf = findPrevKeyframe(keyframes, localFrame);
  const nextKf = findNextKeyframe(keyframes, localFrame);

  return (
    <div className="flex items-center gap-4xs">
      <ToolButton
        size="sm"
        state={prevKf ? 'default' : 'disabled'}
        onClick={() => {
          if (prevKf) onSeekToLocalFrame(prevKf.frame);
        }}
        aria-label={t('Editor.inspector.position-keyframes.prev')}
        isShowTooltip
        tooltipNode={t('Editor.inspector.position-keyframes.prev')}
      >
        <PreviousKeyframeFill />
      </ToolButton>

      <ToolButton
        size="sm"
        state={isInRange ? 'default' : 'disabled'}
        onClick={() => {
          if (!isInRange) return;
          if (hasKf) onRemoveAt(localFrame);
          else onAddAt(localFrame);
        }}
        aria-label={
          hasKf
            ? t('Editor.inspector.position-keyframes.toggle-remove')
            : t('Editor.inspector.position-keyframes.toggle-add')
        }
        isShowTooltip
        tooltipNode={
          hasKf
            ? t('Editor.inspector.position-keyframes.toggle-remove')
            : t('Editor.inspector.position-keyframes.toggle-add')
        }
      >
        <KeyframeDiamond
          filled={hasKf}
          className={
            !isInRange
              ? undefined
              : hasKf
                ? 'text-sys-cont-primary-default'
                : 'text-sys-cont-neutral-light'
          }
        />
      </ToolButton>

      <ToolButton
        size="sm"
        state={nextKf ? 'default' : 'disabled'}
        onClick={() => {
          if (nextKf) onSeekToLocalFrame(nextKf.frame);
        }}
        aria-label={t('Editor.inspector.position-keyframes.next')}
        isShowTooltip
        tooltipNode={t('Editor.inspector.position-keyframes.next')}
      >
        <NextKeyframeFill />
      </ToolButton>

      {keyframes.length > 0 && (
        <div className="flex min-w-0 flex-1 justify-end">
          <ToolButton
            size="sm"
            onClick={onRemoveAll}
            aria-label={t('Editor.inspector.position-keyframes.remove-all')}
            isShowTooltip
            tooltipNode={t('Editor.inspector.position-keyframes.remove-all')}
          >
            <Delete />
          </ToolButton>
        </div>
      )}
    </div>
  );
};

/**
 * 현재 플레이헤드 시점의 오프셋 편집 — CapCut 식. 값을 바꾸면 그 시점에 키프레임이
 * 자동 생성/갱신된다(auto-keyframe). 키프레임 목록 대신 이 한 행만 노출한다.
 * 플레이헤드 구독은 이 소형 컴포넌트에만 건다.
 */
const PositionCurrentValueEditorUnmemoized: React.FC<{
  keyframes: PositionKeyframe[];
  from: number;
  maxLocalFrame: number;
  onUpsertAt: (
    localFrame: number,
    dx: number,
    dy: number,
    commitToUndoStack: boolean,
  ) => void;
}> = ({ keyframes, from, maxLocalFrame, onUpsertAt }) => {
  const t = useTranslations();
  const globalFrame = useCurrentPlayerFrame();
  const localFrame = Math.round(globalFrame - from);
  const isInRange = localFrame >= 0 && localFrame <= maxLocalFrame;
  const { dx, dy } = getPositionOffsetAt(keyframes, localFrame);
  const hasKf = isInRange && hasKeyframeAt(keyframes, localFrame);

  return (
    <div className="flex items-center gap-4xs">
      <KeyframeDiamond
        filled={hasKf}
        className={`size-2 shrink-0 ${
          hasKf
            ? 'text-sys-cont-primary-default'
            : 'text-sys-cont-neutral-light'
        }`}
      />
      <ValueInputField
        value={Math.round(dx)}
        setValue={(update) => {
          if (isInRange)
            onUpsertAt(localFrame, update.num, dy, update.commitToUndoStack);
        }}
        min={-4000}
        max={4000}
        step={1}
        isUnit="px"
        showStepper={false}
        disabled={!isInRange}
        accessibilityLabel={t('Editor.inspector.position-keyframes.dx')}
        className="w-[72px] shrink-0"
      />
      <ValueInputField
        value={Math.round(dy)}
        setValue={(update) => {
          if (isInRange)
            onUpsertAt(localFrame, dx, update.num, update.commitToUndoStack);
        }}
        min={-4000}
        max={4000}
        step={1}
        isUnit="px"
        showStepper={false}
        disabled={!isInRange}
        accessibilityLabel={t('Editor.inspector.position-keyframes.dy')}
        className="w-[72px] shrink-0"
      />
    </div>
  );
};

const PositionCurrentValueEditor = memo(PositionCurrentValueEditorUnmemoized);

/**
 * 위치 키프레임 편집 — "이 시점에 여기" 를 기록하는 CapCut 식 이동.
 *
 * - ◆/◇ 토글: 플레이헤드(아이템 로컬 프레임, 반올림)에 키프레임이 있으면 삭제, 없으면
 *   그 시점 보간값 그대로 추가. 플레이헤드가 아이템 구간 밖이면 disabled.
 * - ‹ › 점프: 현재 프레임 기준 이전/다음 키프레임으로 시크(대상 없으면 disabled).
 * - 🗑 전체삭제: positionKeyframes 필드 제거(빈 배열 commit → undo 1회로 복구).
 * - 저장은 아이템의 positionKeyframes 필드(1급 데이터, 애니 프리셋과 독립).
 * - v1 은 단일 아이템 전용 — 다중 선택에서는 호출부(AnimationControls)가 숨긴다.
 */
const PositionKeyframeControlsUnmemoized: React.FC<{
  itemId: string;
  durationInFrames: number;
}> = ({ itemId, durationInFrames }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const playerRef = useContext(PlayerRefContext);
  const item = useItem(itemId);
  const { items } = useAllItems();

  const keyframes = useMemo<PositionKeyframe[]>(
    () =>
      item &&
      'positionKeyframes' in item &&
      Array.isArray(item.positionKeyframes)
        ? item.positionKeyframes
        : [],
    [item],
  );

  // 아이템(그룹이면 children union) 시작 프레임 — 로컬↔글로벌 프레임 변환 기준.
  const from = useMemo(() => {
    if (!item) return 0;
    return item.type === 'group'
      ? resolveGroupGeometry(item, items).from
      : item.from;
  }, [item, items]);

  const commit = useCallback(
    (next: PositionKeyframe[], commitToUndoStack = true) => {
      setState({
        groupPropertyEdit: 'animation',
        update: (state) =>
          changeItem(state, itemId, (i) => {
            const prev = i as EditorStarterItem & {
              positionKeyframes?: PositionKeyframe[];
            };
            if (next.length === 0) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { positionKeyframes: _removed, ...rest } = prev;
              return rest as EditorStarterItem;
            }
            return { ...prev, positionKeyframes: next };
          }),
        commitToUndoStack,
      });
    },
    [setState, itemId],
  );

  // 로컬 프레임으로 시크 — 그룹/아이템 from 을 더해 글로벌 프레임으로 변환.
  const seekToLocalFrame = useCallback(
    (localFrame: number) => {
      playerRef?.current?.seekTo(Math.round(from + localFrame));
    },
    [playerRef, from],
  );

  // 추가 — 그 시점 곡선 값을 그대로 기록(추가 자체는 화면을 안 바꾼다).
  const addAt = useCallback(
    (localFrame: number) => {
      const { dx, dy } = getPositionOffsetAt(keyframes, localFrame);
      commit(upsertPositionKeyframe(keyframes, { frame: localFrame, dx, dy }));
    },
    [keyframes, commit],
  );

  const removeAt = useCallback(
    (localFrame: number) => {
      commit(removeKeyframeAt(keyframes, localFrame));
    },
    [keyframes, commit],
  );

  const removeAll = useCallback(() => {
    commit([]);
  }, [commit]);

  // CapCut 식 auto-keyframe — 현재 시점 값 편집이 곧 그 시점 키프레임 upsert.
  const upsertAt = useCallback(
    (
      localFrame: number,
      dx: number,
      dy: number,
      commitToUndoStack: boolean,
    ) => {
      commit(
        upsertPositionKeyframe(keyframes, { frame: localFrame, dx, dy }),
        commitToUndoStack,
      );
    },
    [keyframes, commit],
  );

  const maxLocalFrame = Math.max(0, durationInFrames - 1);

  // 섹션 제목은 호출부(AnimationControls)의 ControlsHeader 가 담당한다.
  return (
    <div className="flex flex-col gap-2xs">
      <PositionKeyframeHeaderControls
        keyframes={keyframes}
        from={from}
        maxLocalFrame={maxLocalFrame}
        onSeekToLocalFrame={seekToLocalFrame}
        onAddAt={addAt}
        onRemoveAt={removeAt}
        onRemoveAll={removeAll}
      />

      <PositionCurrentValueEditor
        keyframes={keyframes}
        from={from}
        maxLocalFrame={maxLocalFrame}
        onUpsertAt={upsertAt}
      />

      {keyframes.length === 0 && (
        <span className="text-caption-small text-sys-cont-neutral-lightest">
          {t('Editor.inspector.position-keyframes.empty')}
        </span>
      )}
    </div>
  );
};

export const PositionKeyframeControls = memo(
  PositionKeyframeControlsUnmemoized,
);
